import React, {FC, useCallback, useState} from "react";
import {Box, Button, Drawer, Stack, SxProps, Typography, useMediaQuery, useTheme, styled} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import { useContext } from "react";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";
import {useUpdateStreakState} from "@/app2/shared/data";

interface StreakDrawerProps {
	open: boolean;
	onContinue: (isStreakResumed: boolean, streak: number) => void;
}

const MaterialUISwitch = styled(Switch)(() => ({
	width: 44,
	height: 26,
	padding: 6,
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 2
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: 22,
		height: 22,
		borderRadius: 10
	},
	"& .MuiSwitch-track": {
		borderRadius: 26 / 2,
		backgroundColor: "#CCCCCC61",
		opacity: 1
	}
}));

const ResumeStreakDrawer: FC<StreakDrawerProps> = ({
	open,
	onContinue
}) => {
	const theme = useTheme();
	const {t} = useSSTranslation();
	const { notify } = useContext(App2NotificationContext);
	const isEqualsOrGreaterThanLarge = useMediaQuery(theme.breakpoints.up("md"));
	const buttonFullWidth = !isEqualsOrGreaterThanLarge;
	const [isStreakResumed, setIsStreakResumed] = useState(false);

	const updateStreakMutation = useUpdateStreakState();

	const handleSubmitApi = useCallback(
		(isStreakResumedToSave: boolean) => {
			if (!open || updateStreakMutation.isPending) return;

			updateStreakMutation.mutate(
				isStreakResumedToSave
					? {isStreakPaused: false, incrementStreakIfEligible: true}
					: {isStreakPaused: true},
				{
					onSuccess: (data) => {
						if (!isStreakResumedToSave) {
							notify(
								t("streak_paused_message","Your streak is paused, but your due dates will continue as scheduled."),
								"Success",
								App2NotificationSeverity.success,
								5000
							);
						} else {
							notify(
								t("streak_unpaused_message","Streak pause removed. "),
								"Success",
								App2NotificationSeverity.success,
								5000
							);
						}
						onContinue(isStreakResumedToSave, data.streak ?? 0);
					},
					onError: () => {
						notify("Failed to update streak", "Error", App2NotificationSeverity.error, 5000);
					}
				}
			);
		},
		[open, updateStreakMutation, notify, t, onContinue]
	);

	const handleCloseDrawer = () => {
		handleSubmitApi(isStreakResumed);
	}

	const drawerStyle : SxProps = {
		"& .MuiDrawer-paper": {
			borderTopLeftRadius: isEqualsOrGreaterThanLarge ? 0 : "32px",
			borderTopRightRadius: isEqualsOrGreaterThanLarge ? 0 : "32px"
		}
	}

	const contentWrapperStyle: SxProps = {
		height: isEqualsOrGreaterThanLarge ? "100vh" : "calc(100vh - 64px)",
		backgroundColor: theme.palette.background.paperElevation2,
		justifyContent: "center",
		borderTop: isEqualsOrGreaterThanLarge ? `5px solid ${theme.palette.primary.main}` : undefined,
		position: "relative"
	}

	const headerStyle: SxProps = {
		width: "100%",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "25px 0",
		position: "absolute",
		top: 0
	}

	const buttonWrapperStyle: SxProps = {
		justifyContent: isEqualsOrGreaterThanLarge ? "flex-end" : "normal",
		alignItems: "center",
		height: "106px",
		borderTop: isEqualsOrGreaterThanLarge ? `1px solid ${theme.palette.divider}` : "none",
		padding: "0 25px"
	}

	const buttonStyle: SxProps = {
		color: theme.palette.primary.contrastText,
		fontSize: "15px",
		height: "42px"
	}

	const congratsLabelStyle: SxProps = {
		fontSize: "24px",
		fontWeight: 400,
		paddingLeft: "20px"
	}

	const contentStyle: SxProps = {
		position: isEqualsOrGreaterThanLarge ? "absolute" : "none", top: "140px",
		justifyContent: "center", alignItems: "center", alignSelf: "center"
	}

	const footerStyle: SxProps = {
		position: isEqualsOrGreaterThanLarge ? "fixed" : "relative",
		bottom: 0,
		width: "100%"
	}

	return (
		<Drawer
			anchor="bottom"
			open={open}
			sx={drawerStyle}
		>
			<Stack
				sx={contentWrapperStyle}
			>
				<Stack direction="row" sx={headerStyle}>
					<Box sx={congratsLabelStyle}>
					</Box>
					<IconButton onClick={handleCloseDrawer} sx={{marginRight: "25px"}}>
						<CloseIcon/>
					</IconButton>
				</Stack>
				<Box sx={contentStyle}>
					<Box sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
						<img
							src="/under-construction.gif"
							alt="A description of the GIF"
							style={{width: "274px", height: "206px", display: "block"}}
						/>
					</Box>
					<Typography sx={{maxWidth: "408px",color: theme.palette.text.primary, textAlign: "center", fontSize: "34px", fontWeight: 600, mt: 4}}>
						{t("streak_unpause_question", "You completed a lesson, but your streak is paused. Do you want to unpause it?")}
					</Typography>
					<Stack direction="row" paddingTop={2} spacing={1} alignItems="center" display={"flex"} justifyContent={"center"}>
						<MaterialUISwitch disabled={updateStreakMutation.isPending} checked={isStreakResumed} onChange={e => {
							setIsStreakResumed(e.target.checked);
						}}/>
						<Typography variant={"body1"}>
							{t("streak_unpause", "Unpause Streak")}
						</Typography>
					</Stack>
				</Box>

				<Box sx={footerStyle}>
					<Stack
						direction="row"
						sx={buttonWrapperStyle}
					>
						<Button
							variant="contained"
							fullWidth={buttonFullWidth}
							sx={buttonStyle}
							onClick={handleCloseDrawer}
							disabled={updateStreakMutation.isPending}
						>
							{t("common_continue", "Continue").toUpperCase()}
						</Button>
					</Stack>
				</Box>
			</Stack>
		</Drawer>
	)
}

export default ResumeStreakDrawer;

